/**
 * ModelFormRows.js
 * Composants de formulaire : SeriesRow, ParamRow, OutputRow
 */
import React from "react";
import {Chip, IconButton, Paper, Stack, TextField, Tooltip} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";

const DATA_TYPES = ["NUMERIC", "BINARY", "CATEGORICAL", "BOOLEAN", "STRING"];
const PARAM_TYPES = ["INTEGER", "FLOAT", "STRING", "BOOLEAN", "SELECT"];

export function SeriesRow({series, index, onRemove, onUpdate, canDelete = true, allNames = [], disabled = false}) {
    const isDuplicate = !disabled && series.name && allNames.filter(n => n === series.name).length > 1;
    return (
        <Paper variant="outlined" sx={{p: 1.5, borderRadius: 1, bgcolor: "action.hover"}}>
            <Stack direction="row" alignItems="center" mb={0.75}>
                <Chip label={`S${index + 1}`} size="small" color="primary" variant="outlined" sx={{fontSize: 11, mr: 1}}/>
                <div style={{flex: 1}}/>
                {canDelete && !disabled && <IconButton size="small" color="error" onClick={onRemove}><DeleteIcon sx={{fontSize: 16}}/></IconButton>}
            </Stack>
            <Stack spacing={1}>
                <Stack direction="row" spacing={1}>
                    <Tooltip title="Identifiant unique de la serie — correspond au parametre du handler (ex: observed, expected)" placement="top" arrow>
                        <TextField label="name" size="small" sx={{flex: 1}} disabled={disabled} error={isDuplicate} helperText={isDuplicate ? "Nom duplique" : ""}
                                   value={series.name ?? ""} onChange={(e) => onUpdate("name", e.target.value)}/>
                    </Tooltip>
                    <Tooltip title="Libelle affiche dans l'UI pour cette serie" placement="top" arrow>
                        <TextField label="label" size="small" sx={{flex: 1}} disabled={disabled}
                                   value={series.label ?? ""} onChange={(e) => onUpdate("label", e.target.value)}/>
                    </Tooltip>
                    <Tooltip title="Cle d'extraction dans les donnees brutes (laisser 'value' par defaut)" placement="top" arrow>
                        <TextField label="columnName" size="small" sx={{flex: 0.8}} disabled={disabled}
                                   value={series.columnName ?? "value"} onChange={(e) => onUpdate("columnName", e.target.value)}/>
                    </Tooltip>
                    <Tooltip title="Type de donnees attendu : NUMERIC pour les calculs, BINARY pour 0/1, CATEGORICAL pour les classes" placement="top" arrow>
                        <TextField select label="dataType" size="small" sx={{flex: 0.8}} disabled={disabled}
                                   value={series.dataType ?? "NUMERIC"} onChange={(e) => onUpdate("dataType", e.target.value)}
                                   slotProps={{select: {native: true}}}>
                            {DATA_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                        </TextField>
                    </Tooltip>
                </Stack>
                <Tooltip title="Description affichee a l'utilisateur pour l'aider a choisir la bonne serie" placement="top" arrow>
                    <TextField label="description" size="small" fullWidth disabled={disabled}
                               value={series.description ?? ""} onChange={(e) => onUpdate("description", e.target.value)}/>
                </Tooltip>
            </Stack>
        </Paper>
    );
}

export function ParamRow({param, index, onRemove, onUpdate, allNames = [], disabled = false}) {
    const isDuplicate = !disabled && param.name && allNames.filter(n => n === param.name).length > 1;
    return (
        <Paper variant="outlined" sx={{p: 1.5, borderRadius: 1, bgcolor: "action.hover"}}>
            <Stack direction="row" alignItems="center" mb={0.75}>
                <Chip label={`P${index + 1}`} size="small" color="secondary" variant="outlined" sx={{fontSize: 11, mr: 1}}/>
                <div style={{flex: 1}}/>
                {!disabled && <IconButton size="small" color="error" onClick={onRemove}><DeleteIcon sx={{fontSize: 16}}/></IconButton>}
            </Stack>
            <Stack spacing={1}>
                <Stack direction="row" spacing={1}>
                    <Tooltip title="Identifiant unique du parametre — utilise comme cle dans params.get('name') dans le handler" placement="top" arrow>
                        <TextField label="name" size="small" sx={{flex: 1}} disabled={disabled} error={isDuplicate} helperText={isDuplicate ? "Nom duplique" : ""}
                                   value={param.name ?? ""} onChange={(e) => onUpdate("name", e.target.value)}/>
                    </Tooltip>
                    <Tooltip title="Libelle affiche dans l'interface de configuration de l'analyse" placement="top" arrow>
                        <TextField label="label" size="small" sx={{flex: 1}} disabled={disabled}
                                   value={param.label ?? ""} onChange={(e) => onUpdate("label", e.target.value)}/>
                    </Tooltip>
                    <Tooltip title="Type de la valeur : INTEGER (entier), FLOAT (decimal), SELECT (liste de choix)" placement="top" arrow>
                        <TextField select label="type" size="small" sx={{flex: 0.7}} disabled={disabled}
                                   value={param.type ?? "FLOAT"} onChange={(e) => onUpdate("type", e.target.value)}
                                   slotProps={{select: {native: true}}}>
                            {PARAM_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                        </TextField>
                    </Tooltip>
                    <Tooltip title="Valeur par defaut si l'utilisateur ne la modifie pas" placement="top" arrow>
                        <TextField label="default" size="small" sx={{flex: 0.7}} disabled={disabled}
                                   value={param.default ?? ""}
                                   onChange={(e) => {
                                       const v = e.target.value;
                                       onUpdate("default", v === "" ? null : isNaN(Number(v)) ? v : Number(v));
                                   }}/>
                    </Tooltip>
                </Stack>
                <Stack direction="row" spacing={1}>
                    <Tooltip title="Valeur minimale autorisee (validation cote UI)" placement="top" arrow>
                        <TextField label="min" type="number" size="small" sx={{flex: 1}} disabled={disabled}
                                   value={param.min ?? ""} onChange={(e) => onUpdate("min", e.target.value === "" ? null : parseFloat(e.target.value))}/>
                    </Tooltip>
                    <Tooltip title="Valeur maximale autorisee (validation cote UI)" placement="top" arrow>
                        <TextField label="max" type="number" size="small" sx={{flex: 1}} disabled={disabled}
                                   value={param.max ?? ""} onChange={(e) => onUpdate("max", e.target.value === "" ? null : parseFloat(e.target.value))}/>
                    </Tooltip>
                    <Tooltip title="Liste de valeurs pour le type SELECT — separees par des virgules" placement="top" arrow>
                        <TextField label="options (virgule separees)" size="small" sx={{flex: 2}} disabled={disabled}
                                   value={(param.options ?? []).join(", ")}
                                   onChange={(e) => onUpdate("options", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
                                   helperText={param.type === "SELECT" ? "Requis pour le type SELECT" : ""}/>
                    </Tooltip>
                </Stack>
                <Tooltip title="Description affichee dans un tooltip a l'utilisateur lors de la configuration" placement="top" arrow>
                    <TextField label="description" size="small" fullWidth disabled={disabled}
                               value={param.description ?? ""} onChange={(e) => onUpdate("description", e.target.value)}/>
                </Tooltip>
            </Stack>
        </Paper>
    );
}

export function OutputRow({output, index, onRemove, onUpdate, allNames = [], disabled = false}) {
    const isDuplicate = !disabled && output.name && allNames.filter(n => n === output.name).length > 1;
    return (
        <Paper variant="outlined" sx={{p: 1.5, borderRadius: 1, bgcolor: "action.hover"}}>
            <Stack direction="row" alignItems="center" mb={0.75}>
                <Chip label={`O${index + 1}`} size="small" color="info" variant="outlined" sx={{fontSize: 11, mr: 1}}/>
                <div style={{flex: 1}}/>
                {!disabled && <IconButton size="small" color="error" onClick={onRemove}><DeleteIcon sx={{fontSize: 16}}/></IconButton>}
            </Stack>
            <Stack spacing={1}>
                <Stack direction="row" spacing={1}>
                    <Tooltip title="Cle du resultat retourne par le handler — correspond a une cle du dict (ex: value, delta)" placement="top" arrow>
                        <TextField label="name" size="small" sx={{flex: 1}} disabled={disabled} error={isDuplicate} helperText={isDuplicate ? "Nom duplique" : ""}
                                   value={output.name ?? ""} onChange={(e) => onUpdate("name", e.target.value)}/>
                    </Tooltip>
                    <Tooltip title="Libelle affiche dans les resultats et les dashboards" placement="top" arrow>
                        <TextField label="label" size="small" sx={{flex: 1}} disabled={disabled}
                                   value={output.label ?? ""} onChange={(e) => onUpdate("label", e.target.value)}/>
                    </Tooltip>
                    <Tooltip title="Unite de mesure affichee (ratio, score, ms, count, indice...)" placement="top" arrow>
                        <TextField label="unit" size="small" sx={{flex: 0.6}} disabled={disabled} placeholder="ratio, ms, …"
                                   value={output.unit ?? ""} onChange={(e) => onUpdate("unit", e.target.value || null)}/>
                    </Tooltip>
                </Stack>
                <Tooltip title="Description du resultat — aide l'utilisateur a interpreter la valeur retournee" placement="top" arrow>
                    <TextField label="description" size="small" fullWidth disabled={disabled}
                               value={output.description ?? ""} onChange={(e) => onUpdate("description", e.target.value)}/>
                </Tooltip>
            </Stack>
        </Paper>
    );
}
